import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead, isSuperAdmin } from '@/lib/permissions';
import type {
  Json,
  SalesChannelKind,
  SalesOrderStatus,
  Tables,
} from '@/types/database';

// ---------------------------------------------------------------------------
// Sales hooks
//
// These power the unified Orders queue (`src/pages/sales/OrdersPage.tsx`)
// and the Shopify integration card (`src/pages/integrations/IntegrationsPage`).
//
// The Sales schema (`supabase/migrations/10_sales.sql`) is read-only for the
// React layer in v1 — orders flow in from `shopify-sync` (service-role,
// migration 56). Mutations from the UI side are limited to triggering an
// on-demand sync and the Shopify connect form; both go through Edge
// Functions, never direct table writes from the browser.
// ---------------------------------------------------------------------------

const ORDERS_KEY = ['sales-orders'];
const CHANNELS_KEY = ['sales-channels'];

/**
 * One row per Shopify order (or future Amazon / B2B order), denormalised
 * with the channel name + kind for the unified queue. Line items are
 * surfaced from `sales_orders.metadata.line_items` rather than `so_lines`
 * for now — see ROADMAP §3.4 + migration 56.
 */
export interface SalesOrderRow {
  id: string;
  so_number: string;
  external_id: string | null;
  channel: {
    id: string | null;
    kind: SalesChannelKind | 'unknown';
    name: string;
  };
  customer_email: string | null;
  customer_name: string | null;
  status: SalesOrderStatus;
  ordered_at: string;
  subtotal_cents: number;
  tax_cents: number;
  shipping_cents: number;
  discount_cents: number;
  total_cents: number;
  currency: string;
  ship_to: ShippingAddress | null;
  bill_to: ShippingAddress | null;
  line_items: OrderLineItem[];
  financial_status: string | null;
  fulfillment_status: string | null;
}

export interface OrderLineItem {
  name: string;
  sku: string | null;
  quantity: number;
  unit_price_cents: number;
  /** Channel-side line id (Shopify line_item id) for matching returns / refunds. */
  external_line_id?: string | null;
}

export interface ShippingAddress {
  name?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  province?: string | null;
  province_code?: string | null;
  country?: string | null;
  country_code?: string | null;
  zip?: string | null;
  phone?: string | null;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function useSalesOrders() {
  const { role, profile, loading } = useAuth();
  const enabled = !loading && canRead(role, 'sales', profile?.section_access);
  return useQuery({
    queryKey: ORDERS_KEY,
    enabled,
    queryFn: async (): Promise<SalesOrderRow[]> => {
      // Join through `sales_channels` so the row carries channel kind/name
      // without a second round-trip; PostgREST resolves the join via the
      // `sales_channel_id` FK declared in migration 10.
      const { data, error } = await supabase
        .from('sales_orders')
        .select(
          `id, so_number, external_id, customer_email, status, ordered_at,
           subtotal_cents, tax_cents, shipping_cents, discount_cents,
           total_cents, currency, ship_to, bill_to, sales_channel_id,
           metadata,
           sales_channels:sales_channel_id ( id, kind, name )`,
        )
        .order('ordered_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []).map(toSalesOrderRow);
    },
  });
}

export function useShopifyChannels() {
  const { role, profile, loading } = useAuth();
  const enabled = !loading && canRead(role, 'sales', profile?.section_access);
  return useQuery({
    queryKey: [...CHANNELS_KEY, 'shopify'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales_channels')
        .select('id, name, is_active, config, created_at')
        .eq('kind', 'shopify')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<
        Pick<Tables<'sales_channels'>, 'id' | 'name' | 'is_active' | 'config' | 'created_at'>
      >;
    },
  });
}

/**
 * Mirror of `useShopifyChannels` for Amazon. Each row corresponds to one
 * authorized selling partner; `config` carries `selling_partner_id`,
 * `marketplace_id`, and `region` (set by `amazon-oauth-callback`).
 */
export function useAmazonChannels() {
  const { role, profile, loading } = useAuth();
  const enabled = !loading && canRead(role, 'sales', profile?.section_access);
  return useQuery({
    queryKey: [...CHANNELS_KEY, 'amazon'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales_channels')
        .select('id, name, is_active, config, created_at')
        .eq('kind', 'amazon')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<
        Pick<Tables<'sales_channels'>, 'id' | 'name' | 'is_active' | 'config' | 'created_at'>
      >;
    },
  });
}

export function useChannelSyncState(salesChannelId: string | null) {
  const { role, loading } = useAuth();
  const enabled = !loading && isSuperAdmin(role) && !!salesChannelId;
  return useQuery({
    queryKey: ['channel-sync-state', salesChannelId],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('channel_sync_state')
        .select('cursor, last_synced_at, last_error')
        .eq('sales_channel_id', salesChannelId!)
        .eq('resource', 'orders')
        .maybeSingle();
      if (error) throw error;
      return data as Pick<
        Tables<'channel_sync_state'>,
        'cursor' | 'last_synced_at' | 'last_error'
      > | null;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Manually trigger a Shopify sync — called from the OrdersPage "Sync now"
 * button. Same Edge Function the 15-min cron hits, so the result is
 * deduped by the `channel_sync_state` cursor either way.
 */
export function useTriggerShopifySync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (salesChannelId?: string) => {
      return invokeEdgeFunction<{ ok: boolean; results: Array<{ channel: string; ordered: number; error?: string }> }>(
        'shopify-sync',
        salesChannelId ? { sales_channel_id: salesChannelId } : {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDERS_KEY });
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: ['channel-sync-state'] });
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

/**
 * Mirror of `useTriggerShopifySync` for Amazon SP-API. Cron pulls every
 * 30 minutes; this powers the on-demand "Sync Amazon" button.
 */
export function useTriggerAmazonSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (salesChannelId?: string) => {
      return invokeEdgeFunction<{ ok: boolean; results: Array<{ channel: string; ordered: number; error?: string }> }>(
        'amazon-sync',
        salesChannelId ? { sales_channel_id: salesChannelId } : {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ORDERS_KEY });
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: ['channel-sync-state'] });
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

/**
 * Connect (or re-key) a Shopify store. Calls the `shopify-connect` Edge
 * Function which smoke-tests the token against Shopify's `/shop.json` and
 * fires an immediate first sync so the OrdersPage lights up.
 */
export function useConnectShopify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      store_domain: string;
      access_token: string;
      channel_name?: string;
    }) => {
      return invokeEdgeFunction<{ ok: boolean; channel_id: string; shop_name: string }>(
        'shopify-connect',
        input,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: ORDERS_KEY });
      qc.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface RawOrderRow {
  id: string;
  so_number: string;
  external_id: string | null;
  customer_email: string | null;
  status: SalesOrderStatus;
  ordered_at: string;
  subtotal_cents: number;
  tax_cents: number;
  shipping_cents: number;
  discount_cents: number;
  total_cents: number;
  currency: string;
  ship_to: Json | null;
  bill_to: Json | null;
  sales_channel_id: string | null;
  metadata: Json | null;
  // PostgREST nests the resolved relation under the foreign-key alias.
  sales_channels:
    | { id: string; kind: SalesChannelKind; name: string }
    | { id: string; kind: SalesChannelKind; name: string }[]
    | null;
}

function toSalesOrderRow(raw: RawOrderRow): SalesOrderRow {
  const channelRel = Array.isArray(raw.sales_channels)
    ? raw.sales_channels[0] ?? null
    : raw.sales_channels;
  const metadata = (raw.metadata as Record<string, unknown> | null) ?? {};
  const lineItems = Array.isArray(metadata.line_items)
    ? (metadata.line_items as Array<Record<string, unknown>>).map((li) => ({
        name: String(li.name ?? li.title ?? 'Item'),
        sku: (li.sku as string | null | undefined) ?? null,
        quantity: Number(li.quantity ?? 0),
        unit_price_cents: Number(li.unit_price_cents ?? 0),
        external_line_id: li.shopify_line_item_id != null ? String(li.shopify_line_item_id) : null,
      }))
    : [];
  const customer = (metadata.customer as Record<string, unknown> | null) ?? null;
  const customerName = customer
    ? [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim() || null
    : null;

  return {
    id: raw.id,
    so_number: raw.so_number,
    external_id: raw.external_id,
    channel: {
      id: channelRel?.id ?? null,
      kind: channelRel?.kind ?? 'unknown',
      name: channelRel?.name ?? labelForKind(channelRel?.kind),
    },
    customer_email: raw.customer_email,
    customer_name: customerName,
    status: raw.status,
    ordered_at: raw.ordered_at,
    subtotal_cents: raw.subtotal_cents,
    tax_cents: raw.tax_cents,
    shipping_cents: raw.shipping_cents,
    discount_cents: raw.discount_cents,
    total_cents: raw.total_cents,
    currency: raw.currency,
    ship_to: (raw.ship_to as ShippingAddress | null) ?? null,
    bill_to: (raw.bill_to as ShippingAddress | null) ?? null,
    line_items: lineItems,
    financial_status: (metadata.financial_status as string | null | undefined) ?? null,
    fulfillment_status: (metadata.fulfillment_status as string | null | undefined) ?? null,
  };
}

function labelForKind(kind: SalesChannelKind | 'unknown' | undefined): string {
  switch (kind) {
    case 'shopify':
      return 'Shopify';
    case 'amazon':
      return 'Amazon';
    case 'b2b_portal':
      return 'B2B portal';
    case 'manual':
      return 'Manual';
    default:
      return 'Unknown';
  }
}

async function invokeEdgeFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const msg = await extractFunctionErrorMessage(error);
    throw new Error(msg);
  }
  return data as T;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as FunctionsHttpError).context as Response | undefined;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}

// Exposed so the OrdersPage / IntegrationsPage can share label strings
// without forking the lookup table.
export const CHANNEL_KIND_LABELS: Record<SalesChannelKind, string> = {
  shopify: 'Shopify',
  amazon: 'Amazon',
  b2b_portal: 'B2B portal',
  manual: 'Manual',
};
